import { Toast, ToastType } from 'types/toasts'

// Types
import {
  SafePoktParams,
  SafePoktUserParams,
  UseSafePoktReturnType,
} from './useSafePOKT.types'

// Utils
import { POKT_PER_NODE_EST } from './useSafePOKT.utils'

interface ParamInfo {
  decimals?: boolean
  notNumber?: boolean
}

export interface GetParam extends ParamInfo {
  key: SafePoktParams | SafePoktUserParams
}

export const HARDCODED_EPOCH = 2
export const HARDCODED_EPOCH_DATE = Date.UTC(2022, 3, 27, 21)

export const DAY_MS = 24 * 60 * 60 * 1000
export const EPOCH_ESTIMATE_MS = 7 * DAY_MS

export const SHARES_PER_NODE = POKT_PER_NODE_EST // 10

 
export const CONTRACT_GETS: GetParam[] = [
  { key: SafePoktParams.CurrentEpoch, decimals: false },
  { key: SafePoktParams.BuyPrice, decimals: true },
  { key: SafePoktParams.NodeDiscount },
  { key: SafePoktParams.HolderActionsEnabled, notNumber: true },
  { key: SafePoktParams.HolderCount },
  { key: SafePoktParams.TotalPOKTStake, decimals: true },
  { key: SafePoktParams.NewInvestments, decimals: true },
  { key: SafePoktParams.NodeCount },
  { key: SafePoktParams.ProtToken, notNumber: true },
  { key: SafePoktParams.TotalInvestmentsInUSDC },
  { key: SafePoktParams.TotalRewardsPOKT, decimals: true },
  { key: SafePoktParams.TotalClaimableRewards, decimals: true },
  { key: SafePoktParams.TotalShares, decimals: true },
  { key: SafePoktParams.EpochDuration },
  { key: SafePoktParams.PoktPerShare, decimals: true },
  { key: SafePoktParams.NextEpochTime, decimals: false },
  { key: SafePoktParams.NextEpochRPS, decimals: true },
  { key: SafePoktParams.BuyThreshHold, decimals: false },
  { key: SafePoktParams.GetNetAPR, decimals: true },
  { key: SafePoktParams.LatestSnapshotIndex, decimals: false },
]

export const USER_PARAMS_DECIMALS: Record<
  SafePoktUserParams,
  ParamInfo | undefined
> = {
  [SafePoktUserParams.Exists]: { decimals: false, notNumber: true },
  [SafePoktUserParams.PoktReward]: { decimals: true },
  [SafePoktUserParams.PoktShareCount]: { decimals: true },
  [SafePoktUserParams.PoktUnHold]: { decimals: true },
  [SafePoktUserParams.PoktUnHoldEpoch]: { decimals: false },
  [SafePoktUserParams.SellShareAmount]: { decimals: true },
  [SafePoktUserParams.SellEpoch]: { decimals: false },
  [SafePoktUserParams.LatestSnapshotIndex]: { decimals: false },
  [SafePoktUserParams.NoRewardThisEpoch]: undefined,
  [SafePoktUserParams.PoktUnHoldClaimable]: undefined,
  [SafePoktUserParams.RewardClaimable]: { decimals: true },
  [SafePoktUserParams.TotalClaimedUSDC]: { decimals: true },
  [SafePoktUserParams.WillingToClaim]: { decimals: false },
  [SafePoktUserParams.CanClaimRewards]: { decimals: false, notNumber: true },
  [SafePoktUserParams.CanUpdatePercent]: { decimals: false, notNumber: true },
  [SafePoktUserParams.DemandPendingBuys]: { decimals: false, notNumber: true },
}

export const useSafePoktDefaultParams = {
  [SafePoktParams.NextEpochTime]: 0,
  [SafePoktParams.NextEpochDate]: 0,
  [SafePoktParams.CurrentEpoch]: 0,
  [SafePoktParams.BuyPrice]: -1,
  [SafePoktParams.HolderCount]: 0,
  [SafePoktParams.NewInvestments]: 0,
  [SafePoktParams.NodeCount]: 0,
  [SafePoktParams.PoktRewards]: 0,
  [SafePoktParams.ProtToken]: '',
  [SafePoktParams.TotalInvestmentsInUSDC]: 0,
  [SafePoktParams.TotalRewardsPOKT]: 0,
  [SafePoktParams.TotalClaimableRewards]: 0,
  [SafePoktParams.TotalPOKTStake]: 0,
  [SafePoktParams.TotalShares]: 0,
  [SafePoktParams.TotalSupply]: 0,
  [SafePoktParams.NodeDiscount]: 0,
  [SafePoktParams.GetNetAPR]: 0,
  [SafePoktParams.EpochDuration]: 1,
  [SafePoktParams.PoktPerShare]: 1,
  [SafePoktParams.HolderActionsEnabled]: false,
  [SafePoktParams.NextEpochRPS]: 0,
  [SafePoktParams.BuyThreshHold]: 1000,
  [SafePoktParams.CurrentDiscount]: 0,
  [SafePoktParams.LatestSnapshotIndex]: 0,
    

  // Users
  [SafePoktUserParams.Exists]: false,
  [SafePoktUserParams.WillingToClaim]: 100,
  [SafePoktUserParams.PoktReward]: 0,
  [SafePoktUserParams.PoktUnHold]: 0,
  [SafePoktUserParams.PoktUnHoldEpoch]: Infinity,
  [SafePoktUserParams.SellShareAmount]: 0,
  [SafePoktUserParams.SellEpoch]: 0,
  [SafePoktUserParams.PoktUnHoldClaimable]: false,
  [SafePoktUserParams.PoktShareCount]: 0,
  [SafePoktUserParams.RewardClaimable]: 0,
  [SafePoktUserParams.TotalClaimedUSDC]: 0,
  [SafePoktUserParams.CanClaimRewards]: false,
  [SafePoktUserParams.CanUpdatePercent]: false,
  [SafePoktUserParams.NoRewardThisEpoch]: false,
  [SafePoktUserParams.LatestSnapshotIndex]: 0,
  [SafePoktUserParams.DemandPendingBuys]: [0,0],
    
}

const notInitializedError = (): never => {
  throw new Error('Contract not initialized yet')
}

export const useSafePoktDefaultReturn: UseSafePoktReturnType = {
  ...useSafePoktDefaultParams,

  // Other
  isLoading: true,

  // Methods
  compoundPoktReward: notInitializedError,
  buyPokt: notInitializedError,
  buyPoktOnDemand: notInitializedError,
  sellSharesOrder: notInitializedError,
  confirmSellOrderClaimed: notInitializedError,
  claimReward: notInitializedError,
  changeClaimPercent: notInitializedError,
  unHoldPoktOrder: notInitializedError,
  //computeDiscount: notInitializedError,
  safePoktHistory: notInitializedError,
}

export const SUCCESS_EPOCH_TOAST: Toast = {
  title: 'New epoch started!',
  message: `The Reward distribution from the previous Epoch has been successfully completed!
    You will be able to claim rewards shortly.
    `,
  type: ToastType.Success,
}
